import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardFooter,
  Media,
  Table,
  Container,
  Row,
  Col,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
// core components
import SimpleHeader from "../../../components/Headers/SimpleHeader.js";
import { serverHost } from "variables/serverdata.js";
import { ThemeContext } from "contexts/ThemeContext.jsx";

function CompanyUsers() {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setPerPage] = useState(10);
  const currentUser = JSON.parse(localStorage.getItem("user"));
  // Filter users based on selected client
  useEffect(() => {
    if (selectedClientId === "dealer") {
      setUsers(allUsers.filter((user) => !user.client_uuid));
    } else if (selectedClientId) {
      setUsers(
        allUsers.filter(
          (user) => user.client?.client_uuid === selectedClientId,
        ),
      );
    } else {
      setUsers(allUsers);
    }
    setCurrentPage(1); // Reset to first page on filter change
  }, [selectedClientId, allUsers]);

  const fetchClients = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem("user"));
      if (!currentUser?.dealer_uuid) {
        console.error("No dealer_uuid found");
        return;
      }

      const queryUrl = `${serverHost}/fetch_clients`;

      const response = await fetch(queryUrl, {
        method: "GET",
        headers: {
          Access: `Bearer ${currentUser.access_token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
          "Accept-Profile": "public",
        },
      });

      if (!response.ok) {
        console.error(
          "Error fetching clients:",
          response.status,
          response.statusText,
        );
        return;
      }

      const data = (await response.json()).data;
      console.log("Clients fetched:", data);
      data.sort((a, b) => a.company_name.localeCompare(b.company_name));
      setClients(data || []);
      console.log(clients);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    if (!currentUser) {
      navigate("/admin/home");
      return;
    }

    // Redirect unauthorized roles
    if (
      !["GLOBAL", "SUPERADMIN", "ADMIN", "MGNT"].includes(currentUser.user_role)
    ) {
      navigate("/admin/home");
      return;
    }

    // Fetch clients list if user is GLOBAL
    if (currentUser.user_role === "GLOBAL") {
      fetchClients();
    }

    fetchUsers();
  }, [navigate]);

  const toggleDeleteModal = (user = null) => {
    setDeleteModal(!deleteModal);
    setUserToDelete(user);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const currentUser = JSON.parse(localStorage.getItem("user"));
      const url = `${serverHost}/delete_user`;
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          Access: "Bearer " + currentUser.access_token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_uuid: userToDelete.user_uuid,
          role: userToDelete.user_role,
        }),
      });

      if (!resp.ok) {
        console.error(`Error: Deletion Failed: ${resp.status}`);
        return;
      }

      setUsers(
        users.filter((user) => user.user_uuid !== userToDelete.user_uuid),
      );
      setDeleteModal(false);
      setUserToDelete(null);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      let currentUser;
      try {
        const userStr = localStorage.getItem("user");
        console.log("Raw user data from localStorage:", userStr);
        currentUser = JSON.parse(userStr);
        console.log("Parsed current user from localStorage:", currentUser);
      } catch (parseError) {
        console.error("Error parsing user data from localStorage:", parseError);
        setLoading(false);
        return;
      }

      if (!currentUser) {
        const errorMsg =
          "No user found in localStorage. Please try logging in again.";
        console.error(errorMsg);
        setError(errorMsg);
        setLoading(false);
        return;
      }

      if (!currentUser.dealer_uuid) {
        const errorMsg =
          "No dealer ID found in user data. Please contact support.";
        console.error(errorMsg);
        setError(errorMsg);
        setLoading(false);
        return;
      }

      console.log("Building query with user role:", currentUser.user_role);
      console.log("User dealer_uuid:", currentUser.dealer_uuid);
      console.log("User client_uuid:", currentUser.client_uuid);

      // Build query URL with filters
      let queryUrl = `${serverHost}/get_users`;

      console.log("Fetching users with URL:", queryUrl);

      const response = await fetch(queryUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${currentUser.access_token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
          "Accept-Profile": "public",
        },
      });

      if (!response.ok) {
        const errorMsg = `Failed to fetch users: ${response.status} ${response.statusText}`;
        console.error(errorMsg);
        setError(errorMsg);
        setLoading(false);
        return;
      }

      const userData = (await response.json()).data;

      console.log("Users data fetched:", userData);

      if (!userData || userData.length === 0) {
        console.log("No users found in the response");
      }
      console.log(userData);
      setAllUsers(userData || []);
      setUsers(userData || []);
      setLoading(false);
    } catch (error) {
      console.error("Error:", error);
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    // Navigate to the profile page with user ID in URL
    console.log("Editing", user);
    navigate(`/admin/profile?id=${user.user_uuid}`);
  };

  const [sortConfig, setSortConfig] = useState({
    key: "first_name",
    direction: "asc",
  });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedUsers = [...users].sort((a, b) => {
      let aVal, bVal;

      switch (key) {
        case "name":
          aVal = `${a.first_name} ${a.last_name}`.toLowerCase();
          bVal = `${b.first_name} ${b.last_name}`.toLowerCase();
          break;
        case "email":
          aVal = a.email_address.toLowerCase();
          bVal = b.email_address.toLowerCase();
          break;
        case "client":
          aVal = a.client?.company_name?.toLowerCase() || "";
          bVal = b.client?.company_name?.toLowerCase() || "";
          break;
        case "role":
          aVal = a.user_role.toLowerCase();
          bVal = b.user_role.toLowerCase();
          break;
        case "created":
          aVal = new Date(a.created_at);
          bVal = new Date(b.created_at);
          break;
        default:
          aVal = "";
          bVal = "";
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    setUsers(sortedUsers);
  };

  return (
    <>
      <SimpleHeader name="Company Users" parentName="My Account" />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card>
              <CardHeader
                style={{
                  backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                  color: isDark ? "#e2e8f0" : "#111",
                }}
                className="border-0"
              >
                {error && (
                  <div className="alert alert-danger" role="alert">
                    <strong>Error:</strong> {error}
                  </div>
                )}
                <Row className="align-items-center">
                  <Col xs="6" className="d-flex align-items-center">
                    <h3 className="mb-0">Company Users</h3>
                    {JSON.parse(localStorage.getItem("user"))?.user_role ===
                      "GLOBAL" && (
                      <UncontrolledDropdown className="ml-2">
                        <DropdownToggle
                          size="sm"
                          color="primary"
                          id="filter-tooltip"
                        >
                          <i className="fas fa-filter mr-2" />
                          <span className="font-weight-bold">
                            {selectedClientId
                              ? clients.find(
                                  (c) => c.client_uuid === selectedClientId,
                                )?.company_name
                              : "All"}
                          </span>
                        </DropdownToggle>
                        <UncontrolledTooltip
                          placement="right"
                          target="filter-tooltip"
                        >
                          Filter by Client
                        </UncontrolledTooltip>

                        <DropdownMenu
                          style={{
                            backgroundColor: isDark ? "#1f2937" : "#fff",
                            color: isDark ? "#e5e7eb" : "#212529",
                          }}
                        >
                          <DropdownItem
                            header
                            style={{
                              color: isDark ? "#9ca3af" : "#6c757d",
                            }}
                          >
                            Filter by Client
                          </DropdownItem>

                          <DropdownItem
                            active={!selectedClientId}
                            onClick={() => setSelectedClientId("")}
                            style={{
                              color: isDark ? "#e5e7eb" : "#212529",
                              backgroundColor:
                                isDark && !selectedClientId
                                  ? "#2563eb"
                                  : "transparent",
                            }}
                            onMouseEnter={(e) => {
                              if (isDark) {
                                e.currentTarget.style.backgroundColor =
                                  "#3b82f6";
                                e.currentTarget.style.color = "#ffffff";
                              } else {
                                e.currentTarget.style.backgroundColor =
                                  "#e2e8f0"; // light hover
                                e.currentTarget.style.color = "#111827";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (isDark) {
                                e.currentTarget.style.backgroundColor =
                                  !selectedClientId ? "#2563eb" : "transparent";
                                e.currentTarget.style.color = "#e5e7eb";
                              } else {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                                e.currentTarget.style.color = "#212529";
                              }
                            }}
                          >
                            All Clients
                          </DropdownItem>

                          {clients.map((client) => (
                            <DropdownItem
                              key={client.client_uuid}
                              active={selectedClientId === client.client_uuid}
                              onClick={() =>
                                setSelectedClientId(client.client_uuid)
                              }
                              style={{
                                color: isDark ? "#e5e7eb" : "#212529",
                                backgroundColor:
                                  isDark &&
                                  selectedClientId === client.client_uuid
                                    ? "#2563eb"
                                    : "transparent",
                              }}
                              onMouseEnter={(e) => {
                                if (isDark) {
                                  e.currentTarget.style.backgroundColor =
                                    "#3b82f6";
                                  e.currentTarget.style.color = "#ffffff";
                                } else {
                                  e.currentTarget.style.backgroundColor =
                                    "#e2e8f0";
                                  e.currentTarget.style.color = "#111827";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (isDark) {
                                  e.currentTarget.style.backgroundColor =
                                    selectedClientId === client.client_uuid
                                      ? "#2563eb"
                                      : "transparent";
                                  e.currentTarget.style.color = "#e5e7eb";
                                } else {
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                                  e.currentTarget.style.color = "#212529";
                                }
                              }}
                            >
                              {client.company_name}
                            </DropdownItem>
                          ))}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    )}
                  </Col>
                  <Col className="text-right" xs="6">
                    {/*<Button
                      className="btn-round btn-icon"
                      color="success" // you can pick another color
                      id="tooltipInvite"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/admin/profile?invite=true"); // redirect to invite page
                      }}
                      size="sm"
                    >
                      <span className="btn-inner--icon mr-1">
                        <i className="fas fa-user-plus" />{" "}
                        {/* Use a different icon if you like
                      </span>
                      <span className="btn-inner--text">Invite</span>
                    </Button>*/}
                    {currentUser?.user_role !== "MGNT" && (
                      <>
                      <Button
                        className="btn-round btn-icon"
                        color="info"
                        href="#pablo"
                        id="tooltip443412080"
                        onClick={(e) => {
                          e.preventDefault();
                          const currentUser = JSON.parse(
                            localStorage.getItem("user"),
                          );
                          // Create an empty user object for new user
                          const newUser = {
                            first_name: "",
                            last_name: "",
                            email_address: "",
                            mobile_phone: "",
                            title: "",
                            client_uuid: "",
                            site_uuid: "",
                            user_role: "",
                            dealer_uuid: currentUser.dealer_uuid,
                            is_new_user: true, // Flag to indicate this is a new user
                          };
                          // For ADMIN users, restrict role options and set client/site automatically
                          if (currentUser.user_role === "ADMIN") {
                            newUser.client_uuid = currentUser.client_uuid;
                            newUser.site_uuid = currentUser.site_uuid || "";
                            newUser.user_role = ""; // Role selection restricted in Profile.js
                          }
                          // For new users, we'll use a special flag in the URL
                          navigate("/admin/profile?new=true");
                        }}
                        size="sm"
                      >
                        <span className="btn-inner--icon mr-1">
                          <i className="fas fa-user-edit" />
                        </span>
                        <span className="btn-inner--text">New</span>
                      </Button>
                    <UncontrolledTooltip delay={0} target="tooltip443412080">
                      Add New User
                    </UncontrolledTooltip>
                    </>
                    )}
                  </Col>
                </Row>
              </CardHeader>

              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th
                      className="sort"
                      data-sort="name"
                      scope="col"
                      onClick={() => handleSort("name")}
                      style={{ cursor: "pointer" }}
                    >
                      Name
                    </th>
                    <th
                      className="sort"
                      data-sort="email"
                      scope="col"
                      onClick={() => handleSort("email")}
                      style={{ cursor: "pointer" }}
                    >
                      Email
                    </th>
                    <th
                      className="sort"
                      data-sort="client"
                      scope="col"
                      onClick={() => handleSort("client")}
                      style={{ cursor: "pointer" }}
                    >
                      Client
                    </th>
                    <th
                      className="sort"
                      data-sort="role"
                      scope="col"
                      onClick={() => handleSort("role")}
                      style={{ cursor: "pointer" }}
                    >
                      Role
                    </th>
                    <th
                      className="sort"
                      data-sort="created"
                      scope="col"
                      onClick={() => handleSort("created")}
                      style={{ cursor: "pointer" }}
                    >
                      Created Date
                    </th>
                    <th scope="col" />
                  </tr>
                </thead>
                <tbody className="list">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        Loading users...
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    users
                      .slice(
                        (currentPage - 1) * itemsPerPage,
                        currentPage * itemsPerPage,
                      )
                      .map((user) => (
                        <tr
                          key={user.user_uuid}
                          onClick={() => handleEditUser(user)}
                          style={{ cursor: "pointer" }}
                          className="hover-bg-light"
                        >
                          <td scope="row">
                            <Media className="align-items-center">
                              <a
                                className="avatar rounded-circle mr-3"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <img
                                  alt="..."
                                  style={{ height: "50px", width: "50px" }}
                                  src={
                                    user.avatar &&
                                    user.avatar !== "null" &&
                                    user.avatar.trim()
                                      ? `${serverHost}/avatars/${user.avatar}?t=${Date.now()}`
                                      : require("assets/img/theme/avatar/vue.jpg")
                                  }
                                />
                              </a>
                              <Media>
                                <span className="name mb-0 text-sm">
                                  {user.first_name} {user.last_name}
                                </span>
                              </Media>
                            </Media>
                          </td>
                          <td className="email">{user.email_address}</td>
                          <td className="client">
                            {user.client?.company_name || "-"}
                          </td>
                          <td className="role">
                            <span className="badge badge-dot mr-4">
                              <i className="bg-info" />
                              {(() => {
                                switch (user.user_role) {
                                  case "GLOBAL":
                                    return "dealer";
                                  case "SUPERADMIN":
                                    return "superadmin";
                                  case "ADMIN":
                                    return "admin";
                                  case "MGNT":
                                    return "manager";
                                  case "USER":
                                    return "user";
                                  default:
                                    return user.user_role.toLowerCase();
                                }
                              })()}
                            </span>
                          </td>
                          <td className="created">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="table-actions">
                            {user.user_uuid !==
                              JSON.parse(localStorage.getItem("user"))
                                .user_uuid && (
                              <>
                                <a
                                  className="table-action table-action-delete"
                                  href="#pablo"
                                  id={`tooltip-delete-${user.user_uuid}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleDeleteModal(user);
                                  }}
                                >
                                  <i className="fas fa-trash fa-lg" />
                                </a>
                                <UncontrolledTooltip
                                  delay={0}
                                  target={`tooltip-delete-${user.user_uuid}`}
                                >
                                  Delete User
                                </UncontrolledTooltip>
                              </>
                            )}
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </Table>

              <CardFooter
                style={{
                  backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                  color: isDark ? "#e2e8f0" : "#111",
                }}
                className="py-4"
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <label  className="d-flex align-items-center mb-0">
                      Show{" "}
                      <select
                        className="form-control form-control-sm mx-2"
                        onChange={(e) => setPerPage(e.target.value)}
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>{" "}
                      entries.
                    </label>
                  </div>
                  <div>
                    <nav aria-label="...">
                      <Pagination
                        className="pagination justify-content-end mb-0"
                        listClassName="justify-content-end mb-0"
                      >
                        <PaginationItem disabled={currentPage === 1}>
                          <PaginationLink
                            href="#pablo"
                            onClick={(e) => {
                              e.preventDefault();
                              if (currentPage > 1)
                                setCurrentPage(currentPage - 1);
                            }}
                            tabIndex="-1"
                          >
                            <i className="fas fa-angle-left" />
                            <span className="sr-only">Previous</span>
                          </PaginationLink>
                        </PaginationItem>
                        {[...Array(Math.ceil(users.length / itemsPerPage))].map(
                          (_, index) => (
                            <PaginationItem
                              active={currentPage === index + 1}
                              key={index}
                            >
                              <PaginationLink
                                href="#pablo"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setCurrentPage(index + 1);
                                }}
                              >
                                {index + 1}
                              </PaginationLink>
                            </PaginationItem>
                          ),
                        )}
                        <PaginationItem
                          disabled={
                            currentPage ===
                            Math.ceil(users.length / itemsPerPage)
                          }
                        >
                          <PaginationLink
                            href="#pablo"
                            onClick={(e) => {
                              e.preventDefault();
                              if (
                                currentPage <
                                Math.ceil(users.length / itemsPerPage)
                              )
                                setCurrentPage(currentPage + 1);
                            }}
                          >
                            <i className="fas fa-angle-right" />
                            <span className="sr-only">Next</span>
                          </PaginationLink>
                        </PaginationItem>
                      </Pagination>
                    </nav>
                  </div>
                </div>
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal} centered>
        <ModalHeader
          toggle={toggleDeleteModal}
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
            color: isDark ? "#e2e8f0" : "#212529",
            borderBottom: "1px solid",
            borderColor: isDark ? "#374151" : "#dee2e6",
          }}
        >
          Delete User
        </ModalHeader>

        <ModalBody
          style={{
            backgroundColor: isDark ? "#111827" : "#fff",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          <div className="text-center">
            <i className="fas fa-exclamation-triangle fa-3x mb-3 text-danger" />

            <p className="mb-2">
              Are you sure you want to delete{" "}
              <span style={{ fontWeight: "bold" }}>
                {userToDelete
                  ? `${userToDelete.first_name} ${userToDelete.last_name}`
                  : ""}
              </span>
              ?
            </p>

            <strong>This action cannot be undone.</strong>

            <p className="text-sm text-black mb-0">
              All user data will be permanently deleted.
            </p>
          </div>
        </ModalBody>

        <ModalFooter
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
            borderTop: "1px solid",
            borderColor: isDark ? "#374151" : "#dee2e6",
          }}
        >
          <Button color="light" onClick={toggleDeleteModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDeleteUser}>
            Delete User
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default CompanyUsers;
